<template>
    <div class="print-only" :id="id">
        <div class="fixed-header">
            <div class="header_white">
                <img class="logo" :src="`/img/${brandLabel}/logos/logo-color.svg`" :alt="`${brandLabel} logo`" />
            </div>
        </div>
        <div v-for="(carDetailsData, carIndex) in (initialCarDetails || carDetailsCollection)" :key="carIndex"
            class="layout__main car-detail content" id="mainWrapper">
            <div style="padding: 0 16px;">
                <div class="intro__info">
                    <h2 class="h2">
                        <span class="ftw-normal">
                            {{
                                carDetailsData?.brand?.name +
                                " " +
                                carDetailsData?.model +
                                " " +
                                carDetailsData?.finish +
                                " " +
                                carDetailsData?.powertrain?.gearbox?.name +
                                " " +
                                carDetailsData?.powertrain?.fueltype?.name
                            }}
                        </span>
                    </h2>
                    <p class="fz-bigger">
                        {{
                            carDetailsData?.powertrain?.motorisation
                                ? carDetailsData?.powertrain?.motorisation
                                : "" + " " + carDetailsData?.finish
                        }}
                    </p>
                    <div class="intro__details">
                        <div class="intro__details-col">
                            <p class="intro__details-title">
                                <i18n-t keypath="carDetails.date" />
                            </p>
                            <p v-if="carDetailsData?.firstregistration_date" class="intro__details-text">
                                {{
                                    getDayFromDate(
                                        carDetailsData?.firstregistration_date,
                                    )
                                }}
                                <span style="font-size: 20px">
                                    {{
                                        getMonthFromDate(
                                            carDetailsData?.firstregistration_date,
                                        )
                                    }}
                                </span>
                                <span v-if="carDetailsData?.firstregistration_date" class="intro__details-num"
                                    style="margin-left: 2px">
                                    {{
                                        getYearFromDate(
                                            carDetailsData?.firstregistration_date,
                                        )
                                    }}
                                </span>
                            </p>
                            <p v-else>-</p>
                        </div>
                        <div class="intro__details-col">
                            <p class="intro__details-title">
                                <i18n-t keypath="carDetails.kmUsed" />
                            </p>
                            <p class="intro__details-text">
                                <span class="intro__details-num">
                                    {{ carDetailsData?.mileage }}
                                </span>
                                km
                            </p>
                        </div>
                        <div v-if="
                            carDetailsData?.powertrain?.specification
                                ?.emission_co2_wltp_combined
                        " class="intro__details-col">
                            <p class="intro__details-title">
                                <i18n-t keypath="carDetails.emissionCO2" />
                            </p>
                            <p class="intro__details-text">
                                <span class="intro__details-num">
                                    {{
                                        // @ts-ignore
                                        carDetailsData?.powertrain?.specification
                                            ?.emission_co2_wltp_combined
                                    }}
                                </span>
                                g/km
                            </p>
                        </div>
                    </div>
                    <div class="intro__info-price price" style="margin-bottom: 16px;">
                        <p class="h2">
                            <template v-if="
                                carDetailsData?.price_current?.list_price_euro
                            ">
                                <app-format-currency :value="carDetailsData?.price_current
                                    ?.list_price_euro || 0
                                    " />
                            </template>
                        </p>
                        <div v-if="carDetailsData?.price_previous" class="price__full">
                            <i18n-t keypath="carDetails.ExclusiveVAT" />
                        </div>
                    </div>
                    <div class="options">
                        <div id="feature-wrapper" class="option"
                            v-for="(option, index) in initialOptions?.[carDetailsData?.id] || []" :key="index">
                            <img id="feature-img" v-if="option.icon_href" :src="option.icon_href" alt="" />
                            <p class="label__spec-name">
                                {{ option.name }}
                            </p>
                        </div>
                    </div>
                    <div class="firstPageImages" style="margin: 16px 0;">
                        <div class="vehicle-image-grid" v-if="images[carDetailsData?.id]?.length">
                            <div v-for="(image, imageIndex) in images[carDetailsData?.id].slice(0, 4)" :key="imageIndex"
                                class="grid-item">
                                <img :src="image.href_small" class="vehicle-print-image"
                                    @load="updateLoadedImages(carDetailsData?.id, image.id)"
                                    :alt="carDetailsData?.brand?.name + ' ' + carDetailsData?.model" width="480"
                                    height="360" />
                            </div>
                        </div>
                    </div>
                    <div class="images" style="margin-top: 45px;">
                        <div class="vehicle-image-grid" v-if="images[carDetailsData?.id]?.length">
                            <div v-for="(image, imageIndex) in images[carDetailsData?.id].slice(4)" :key="imageIndex"
                                class="grid-item">
                                <img :src="image.href_small" class="vehicle-print-image"
                                    @load="updateLoadedImages(carDetailsData?.id, image.id)"
                                    :alt="carDetailsData?.brand?.name + ' ' + carDetailsData?.model" width="480"
                                    height="360" />
                            </div>
                        </div>
                    </div>
                    <div style="page-break-after: always"></div>
                </div>
                <div class="block" ref="detail" id="detail">
                    <h3 class="h3 ttu">
                        <i18n-t keypath="carDetails.car" />
                        <span>&nbsp;</span>
                        <span class="word-blue">
                            <i18n-t keypath="carDetails.details" />
                        </span>
                    </h3>
                    <div class="detail__row" style="flex-direction: column;">
                        <div class="detail__col">
                            <div class="detail__content" v-for="group in (carDetailsColumn(carDetailsData, 0))">
                                <h4 class="detail__title">
                                    {{ group.title }}
                                    <span v-if="group.info" class="detail__tooltip" v-tippy="{
                                        content: group.info,
                                        placement: 'right',
                                        interactive: true,
                                    }">
                                        <svg class="sprite-icon info" width="13" height="13">
                                            <use xlink:href="/img/sprite.svg#info-question"></use>
                                        </svg>
                                    </span>
                                </h4>
                                <div class="detail__item" v-for="item in group.items">
                                    <template v-if="item.name === 'vehicle_message'">
                                        <div class="vehicle_message_container">
                                            <p>
                                            {{ item.value }}
                                            </p>
                                        </div>
                                    </template>
                                    <template v-else>
                                    <div class="detail__name">
                                        {{ item.name }}
                                    </div>
                                        <div class="detail__value">
                                            {{ item.value }}
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="block">
                    <div class="detail__row" style="flex-direction: column;">
                        <div class="detail__col">
                            <div class="detail__content" v-for="group in (carDetailsColumn(carDetailsData, 1))">
                                <h4 class="detail__title">
                                    {{ group.title }}
                                    <span v-if="group.info" class="detail__tooltip" v-tippy="{
                                        content: group.info,
                                        placement: 'right',
                                        interactive: true,
                                    }">
                                        <svg class="sprite-icon info" width="13" height="13">
                                            <use xlink:href="/img/sprite.svg#info-question"></use>
                                        </svg>
                                    </span>
                                </h4>
                                <div class="detail__item" v-for="item in group.items">
                                    <template v-if="item.name === 'vehicle_message'">
                                        <div class="vehicle_message_container">
                                            <p v-html="item.value"/>
                                        </div>
                                    </template>
                                    <template v-else>
                                    <div class="detail__name">
                                        {{ item.name }}
                                    </div>
                                        <div class="detail__value">
                                            {{ item.value }}
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="block">
                    <h3 class="h3 ttu mb">
                        <i18n-t keypath="carDetails.main" />
                        <span>&nbsp;</span>
                        <span class="word-blue">
                            <i18n-t keypath="carDetails.features" />
                        </span>
                    </h3>
                    <div class="col-lg-10 features__list mb" id="main-features">
                        <app-vehicle-options :vehicle-id="carDetailsData?.id" :show-label="true" :size="38"
                            disableWowEffect="true" />
                    </div>
                    <div v-if="isCarconnexPlusLabel(carDetailsData)" class="specifications__block" ref="carconnexTitle"
                        id="carconnexTitle">
                        <h3 class="h3 ttu">
                            {{ brandLabelText }}
                            <span class="word-blue">+</span>
                        </h3>
                        <p>
                            <i18n-t keypath="carDetails.carconnexTitle">
                                <template v-slot:brandLabelText>
                                    <span>{{ brandLabelText }}</span>
                                </template>
                            </i18n-t>
                        </p>
                    </div>
                    <div style="margin-top: 16px;">
                        <div>
                            <h3 class="h3 ttu">
                                <i18n-t keypath="carDetails.documents" />
                            </h3>
                            <ul class="list">
                                <li v-if="vehicleDocumentAvailability?.[carDetailsData?.id]?.length"
                                    v-for="(dto, index) in vehicleDocumentAvailability?.[carDetailsData?.id]"
                                    v-bind:key="index" class="list-item fz-smaller">
                                    {{ dto.documenttype.name }}:
                                    <i18n-t v-if="dto.available" keypath="carDetails.available" />
                                    <i18n-t v-else keypath="myOrders.notAvailable" />
                                    {{ dto.from_date !== undefined ? "(" + dto.from_date + ")" : "" }}
                                </li>
                                <li v-else class="list-item fz-smaller">
                                    <i18n-t keypath="carDetails.documentAreNotAvailable" />
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="h3 ttu">
                                <i18n-t keypath="carDetails.accessories" />
                            </h3>
                            <ul class="list">
                                <li v-if="vehicleAccessoryAvailability?.[carDetailsData?.id]?.length"
                                    v-for="(dto, index) in vehicleAccessoryAvailability?.[carDetailsData?.id]"
                                    v-bind:key="index" class="list-item fz-smaller">
                                    {{ dto.accessory.name }}:
                                    <i18n-t v-if="dto.available" keypath="carDetails.available" />
                                    <i18n-t v-else keypath="myOrders.notAvailable" />
                                </li>
                                <li v-else class="list-item fz-smaller">
                                    <i18n-t keypath="carDetails.accessoriesAreNotAvailable" />
                                </li>
                            </ul>
                        </div>
                    </div>

                </div>
                <div class="block">
                    <h3 class="h3 ttu mb">
                        <i18n-t keypath="carDetails.full" />
                        <span>&nbsp;</span>
                        <span class="word-blue">
                            <i18n-t keypath="carDetails.equipment" />
                        </span>
                    </h3>
                    <div style="display: flex; gap: 16px; flex-direction: column;">
                        <div v-if="vehicleFactoryEquipments[carDetailsData?.id]?.length">
                            <ul class="list">
                                <li class="list-item fz-smaller"
                                    v-for="item in vehicleFactoryEquipments[carDetailsData?.id]" :key="item">
                                    {{ item }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="block">
                    <div v-if="vehicleDamageRecord[carDetailsData?.id]?.length !== 0">
                        <div class="damages__wrapper">
                            <img class="damages__car" src="/img/car-detail/car-damage.svg" alt="Car" />
                            <div class="pin__wrap">
                                <template v-for="(record, index) in vehicleDamageRecord[carDetailsData?.id]"
                                    v-bind:key="index">
                                    <vue-tippy tag="div" :max-width="300" class="pin tooltip__info" content-tag="div"
                                        :class="'p' + record.location.id"
                                        :data-category="(record.types[0]?.name || '').toLowerCase()"
                                        :data-number="record.location.id">
                                        <template #content>
                                            <template v-for="(type, index) in record.types" v-bind:key="index">
                                                <div class="car-detail">
                                                    <div class="damages">
                                                        <div class="p-d-flex p-flex-colmn p-ai-center">
                                                            <ul class="pin__marks p-d-flex p-m-0">
                                                                <li class="pin__mark p-m-0" :data-cat="type.id">
                                                                    <span class="pin__mark-title">
                                                                        {{ type.name }}
                                                                    </span>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        <!-- <div class="p-pl-2">
                                            {{ sameDamage.description }}
                                        </div> -->
                                                    </div>
                                                    <p-divider v-if="record.types.length > 1"
                                                        class="p-my-2"></p-divider>
                                                </div>
                                            </template>
                                        </template>
                                    </vue-tippy>
                                </template>
                            </div>
                        </div>
                        <ul class="pin__marks">
                            <li v-if="isDamageLabel(1, carDetailsData?.id)" class="pin__mark" data-cat="crushed">
                                <span class="pin__mark-title">
                                    <i18n-t keypath="carDamage.crushed" />
                                    :
                                </span>
                                <template v-for="(record, index) in vehicleDamageRecord[carDetailsData?.id]"
                                    v-bind:key="index">
                                    <span v-if="
                                        record.types.find((type) => {
                                            return type.id === 3;
                                        })
                                    " class="pin__mark-num">
                                        {{ record.location.id }},
                                    </span>
                                </template>
                            </li>
                            <li v-if="isDamageLabel(2, carDetailsData?.id)" class="pin__mark" data-cat="scratch">
                                <span class="pin__mark-title">
                                    <i18n-t keypath="carDamage.scratch" />
                                    :
                                </span>
                                <template v-for="(record, index) in vehicleDamageRecord[carDetailsData?.id]"
                                    v-bind:key="index">
                                    <span v-if="
                                        record.types.find((type) => {
                                            return type.id === 2;
                                        })
                                    " class="pin__mark-num">
                                        {{ record.location.id }},
                                    </span>
                                </template>
                            </li>
                            <li v-if="isDamageLabel(1, carDetailsData?.id)" class="pin__mark" data-cat="dent">
                                <span class="pin__mark-title">
                                    <i18n-t keypath="carDamage.dent" />
                                    :
                                </span>
                                <template v-for="(record, index) in vehicleDamageRecord[carDetailsData?.id]"
                                    v-bind:key="index">
                                    <span v-if="
                                        record.types.find((type) => {
                                            return type.id === 1;
                                        })
                                    " class="pin__mark-num">
                                        {{ record.location.id }},
                                    </span>
                                </template>
                            </li>
                            <li v-if="isDamageLabel(4, carDetailsData?.id)" class="pin__mark" data-cat="break">
                                <span class="pin__mark-title">
                                    <i18n-t keypath="carDamage.break" />
                                    :
                                </span>
                                <template v-for="(record, index) in vehicleDamageRecord[carDetailsData?.id]"
                                    v-bind:key="index">
                                    <span v-if="
                                        record.types.find((type) => {
                                            return type.id === 4;
                                        })
                                    " class="pin__mark-num">
                                        {{ record.location.id }},
                                    </span>
                                </template>
                            </li>
                            <li v-if="isDamageLabel(5, carDetailsData?.id)" class="pin__mark" data-cat="others">
                                <span class="pin__mark-title">
                                    <i18n-t keypath="carDamage.others" />
                                    :
                                </span>
                                <template v-for="(record, index) in vehicleDamageRecord[carDetailsData?.id]"
                                    v-bind:key="index">
                                    <span v-if="
                                        record.types.find((type) => {
                                            return type.id === 5;
                                        })
                                    " class="pin__mark-num">
                                        {{ record.location.id }},
                                    </span>
                                </template>
                            </li>
                        </ul>
                    </div>
                    <div class="fz-bigger" v-else>
                        <div class="damages__wrapper">
                            <img class="damages__car" src="/img/car-detail/car-damage.svg" alt="Car" width="100"
                                height="100" />
                            <div class="damages__success" />
                        </div>
                    </div>
                </div>

            </div>

            <div v-if="carIndex < carDetailsCollection.length - 1" style="page-break-after: always"></div>
        </div>
    </div>
</template>

<script lang="ts">
import { AppVehiclesPrint } from "./app-vehicles-print";

export default AppVehiclesPrint;
</script>

<style lang="scss">
.mb {
    margin-bottom: 16px;
}

.fixed-header {
    padding: 16px;
}

.vehicle-image-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin: 16px 0;
}


.grid-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-color: #f4f8fd;
}

.options {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.option {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
    position: relative;
    padding: 0.6rem 0.7333333333rem;
    min-width: 1.6rem;
    min-height: 1.6rem;
    color: #627690;
    border: 1px solid rgba(98, 118, 144, .5);
    border-radius: 2rem;
}

.print-only {
    display: none;
}

.block {
    page-break-before: always;
    margin-top: 20px;

    &__nobreak {
        page-break-before: avoid;
    }
}


.damages {
    &__wrapper {
        width: 100%;
        margin: 0 auto;
        max-width: 800px;
        position: relative;

        @media (max-width: 767px) {
            margin: 10vh auto;
        }

        &:before {
            content: "";
            display: block;
            width: 100%;
            padding-bottom: 60%;
        }
    }

    &__car {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        object-fit: cover;
        object-position: center bottom;


    }

    &__success {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        display: flex;

        &:before {
            content: "no registered damage";
            display: flex;
            align-content: center;
            align-items: center;
            justify-content: center;
            margin: auto;
            width: 300px;
            height: 300px;
            border-radius: 50%;
            border: 15px solid #00c977;
            color: #00c977;
            letter-spacing: 2px;
            text-align: center;
            font-weight: bold;
            text-transform: uppercase;
            font-size: 32px;
        }
    }
}

@media print {
    @page {
        size: A4;
        margin: 5mm 0 0 0;
    }

    li {
        //bullets
        list-style-type: disc;
        margin-left: 16px;
    }

    .vehicle-image-grid {
        break-inside: avoid;
        page-break-inside: avoid;
    }

    .print-only {
        display: block;
    }
}

.vehicle_message_container {
    border: 1px solid #ff7777;
    padding: 10px;
    border-radius: 5px;
    margin-top: 20px;
    width: 100%;
    color: #ff7777;
}
</style>
